// revealElement — "open the material at element E": scroll E's block into view
// and give it a brief, calm highlight. A view-local DOM effect keyed on the
// FocusTarget's identity (a new open = a new target object, so re-opening the
// same item reveals it again); it decides nothing and writes no app state.
//
// The target may name a node the document does not render as its own block:
// containers (a section) flatten away in the editor's element list. Such a
// target resolves to the FIRST rendered block whose domain path passes through
// it (`pathById`, root→node ids).

export { revealedElementId, useRevealTarget };

import { type RefObject, useEffect } from "react";
import { focusElementOf } from "../../Navigation/focusTarget.js";
import type { FocusTarget } from "../../Navigation/types.js";
import { centerInScrollParent } from "../../scrollParent.js";
import type { Element, ElementId } from "../types.js";

const REVEALED_CLASS = "bilda-material-document__block--revealed";

// Pure: the rendered block a target element resolves to, if any.
const revealedElementId = (
    elements: readonly Element[],
    pathById: ReadonlyMap<ElementId, readonly string[]> | undefined,
    targetId: string,
): ElementId | undefined =>
    elements.find(
        (e) =>
            e.id === targetId ||
            (pathById?.get(e.id)?.includes(targetId) ?? false),
    )?.id;

const useRevealTarget = (
    root: RefObject<HTMLElement | null>,
    elements: readonly Element[],
    pathById: ReadonlyMap<ElementId, readonly string[]> | undefined,
    target: FocusTarget | undefined,
): void => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the target alone; a re-render with new elements must not re-scroll.
    useEffect(() => {
        const targetId = focusElementOf(target);
        if (targetId === undefined) return;
        const id = revealedElementId(elements, pathById, targetId);
        const block =
            id === undefined
                ? undefined
                : Array.from(
                      root.current?.querySelectorAll<HTMLElement>(
                          "[data-element-id]",
                      ) ?? [],
                  ).find((el) => el.dataset.elementId === id);
        if (block === undefined) return;
        centerInScrollParent(block);
        block.classList.add(REVEALED_CLASS);
        const clear = () => block.classList.remove(REVEALED_CLASS);
        block.addEventListener("animationend", clear, { once: true });
        return () => {
            block.removeEventListener("animationend", clear);
            clear();
        };
    }, [target]);
};
